/**
 * DeepLinkHandler - Always rendered, regardless of auth state
 * Handles deep links for native app (magic links, conversation navigation)
 *
 * This component is rendered at the app root to ensure deep links
 * are handled even when the user is logged out.
 */

import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { App } from '@capacitor/app';
import { isNative } from '@/lib/capacitor';

// Type for the appUrlOpen event - defined here to avoid import issues
interface URLOpenListenerEvent {
  url: string;
}
import { useToast } from '@/app/providers/ToastContext';
import { useSession } from '@/features/auth/hooks/useSession';

const PENDING_DEEPLINK_KEY = 'xavior_pending_deeplink';
const PROCESSED_DEEPLINK_KEY = 'xavior_processed_deeplink';

/**
 * Store a pending deep link for processing after auth
 */
export function storePendingDeepLink(path: string): void {
  sessionStorage.setItem(PENDING_DEEPLINK_KEY, path);
}

/**
 * Get and clear the pending deep link
 */
export function consumePendingDeepLink(): string | null {
  const pending = sessionStorage.getItem(PENDING_DEEPLINK_KEY);
  if (pending) {
    sessionStorage.removeItem(PENDING_DEEPLINK_KEY);
  }
  return pending;
}

/**
 * Check if there's a pending deep link
 */
export function hasPendingDeepLink(): boolean {
  return sessionStorage.getItem(PENDING_DEEPLINK_KEY) !== null;
}

/**
 * Check if a URL has already been processed (prevents re-triggering loops)
 */
function isUrlProcessed(url: string): boolean {
  const processed = sessionStorage.getItem(PROCESSED_DEEPLINK_KEY);
  return processed === url;
}

/**
 * Mark a URL as processed
 */
function markUrlProcessed(url: string): void {
  sessionStorage.setItem(PROCESSED_DEEPLINK_KEY, url);
}

/**
 * Clear the processed URL marker (call on logout or when appropriate)
 */
export function clearProcessedDeepLink(): void {
  sessionStorage.removeItem(PROCESSED_DEEPLINK_KEY);
}

export function DeepLinkHandler() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { status } = useSession();

  // Use refs to avoid re-registering listeners when these change
  const statusRef = useRef(status);
  const navigateRef = useRef(navigate);
  const showToastRef = useRef(showToast);

  // Track if we've already checked the launch URL this session
  const hasCheckedLaunchUrl = useRef(false);

  // Keep refs up to date
  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  useEffect(() => {
    showToastRef.current = showToast;
  }, [showToast]);

  // Stable handler using refs - won't cause effect re-runs
  const handleDeepLink = useCallback((event: URLOpenListenerEvent) => {
    const url = event.url;
    console.log('[DeepLinkHandler] App opened with URL:', url);

    // Check if we've already processed this exact URL
    if (isUrlProcessed(url)) {
      console.log('[DeepLinkHandler] URL already processed, skipping:', url);
      return;
    }

    try {
      // Parse the URL - handles both xavior:// and https://app.xavior.ai/
      let urlToParse = url;
      if (urlToParse.startsWith('xavior://')) {
        urlToParse = urlToParse.replace('xavior://', 'https://app.xavior.ai/');
      }

      const parsedUrl = new URL(urlToParse);
      const path = parsedUrl.pathname + parsedUrl.search;
      const currentStatus = statusRef.current;

      console.log('[DeepLinkHandler] Parsed path:', path, 'Auth status:', currentStatus);

      // If it's a magic link verification, navigate directly
      // The MagicLinkVerify page handles auth itself
      if (path.startsWith('/auth/verify')) {
        // Mark as processed BEFORE navigating to prevent re-processing
        markUrlProcessed(url);
        showToastRef.current('Magic link detected, verifying...', 'info');
        navigateRef.current(path);
        return;
      }

      // For other links, check auth status
      if (currentStatus === 'authenticated') {
        // User is authenticated, navigate immediately
        console.log('[DeepLinkHandler] Authenticated, navigating to:', path);
        markUrlProcessed(url);
        navigateRef.current(path);
      } else {
        // User is not authenticated, store for later
        console.log('[DeepLinkHandler] Not authenticated, storing pending link:', path);
        storePendingDeepLink(path);
        markUrlProcessed(url);
        showToastRef.current('Please log in to continue', 'info');
      }
    } catch (error) {
      console.error('[DeepLinkHandler] Failed to parse URL:', url, error);
    }
  }, []);

  // Set up deep link listener - only once on mount
  useEffect(() => {
    if (!isNative()) return;

    // Listen for deep links when app is running
    App.addListener('appUrlOpen', handleDeepLink);

    return () => {
      App.removeAllListeners();
    };
  }, [handleDeepLink]);

  // Check launch URL - only once on mount
  useEffect(() => {
    if (!isNative()) return;
    if (hasCheckedLaunchUrl.current) return;
    hasCheckedLaunchUrl.current = true;

    App.getLaunchUrl().then((result) => {
      if (result?.url) {
        console.log('[DeepLinkHandler] App launched with URL:', result.url);
        handleDeepLink({ url: result.url });
      }
    });
  }, [handleDeepLink]);

  // Process pending deep link after auth completes
  useEffect(() => {
    if (status !== 'authenticated') return;

    const pending = consumePendingDeepLink();
    if (pending) {
      console.log('[DeepLinkHandler] Processing pending deep link after auth:', pending);
      // Small delay to ensure session is fully established
      setTimeout(() => {
        navigateRef.current(pending);
      }, 100);
    }
  }, [status]);

  return null;
}
